import SEO from "@/components/SEO";
import { PageHero, Section, SectionHead, FAQ } from "@/components/sections";
import { Reveal } from "@/components/Reveal";
import { CTALink } from "@/components/ui-kit";
import LeadForm from "@/components/LeadForm";
import { Landmark, Wallet, ShieldCheck, TrendingUp } from "lucide-react";

const services = [
  { icon: Landmark, name: "Business Loans", desc: "Term loans and structured credit to fund expansion, equipment and acquisitions — matched to your cash-flow cycle, not a generic template." },
  { icon: Wallet, name: "Working Capital", desc: "Cash-credit, overdraft and invoice-backed lines to smooth the gap between payables and receivables without over-leveraging." },
  { icon: TrendingUp, name: "Secured & High-Yield Bonds", desc: "Access to secured and higher-yield corporate bonds for treasury deployment, with risk and liquidity clearly explained." },
  { icon: ShieldCheck, name: "Corporate Insurance", desc: "Group health, keyman, liability and asset cover so a single event doesn't threaten the balance sheet. Insurance is the subject matter of solicitation." },
];

const faq = [
  { q: "Do you only work with large companies?", a: "No. We work with founders, SMEs and established firms. The approach scales — the discipline stays the same." },
  { q: "Is the bond allocation guaranteed?", a: "No investment is guaranteed. We explain the security, issuer and liquidity of each bond so you can decide. All returns are potential, subject to market and credit risk." },
  { q: "Can you coordinate with our CA or CFO?", a: "Yes. We regularly work alongside your finance team so lending, treasury and insurance decisions fit your wider plan." },
];

export default function Business() {
  return (
    <div>
      <SEO path="/business" title="Financial Solutions for Businesses" description="Business loans, working capital, secured & high-yield bonds and corporate insurance — structured around your cash flows by Rao Investment Solutions." faq={faq} />
      <PageHero
        label="For Businesses"
        title="Fund growth. Protect the downside. Put treasury to work."
        intro="A single desk for corporate credit, capital deployment and risk cover — sized to your business, reviewed as it grows."
        breadcrumbs={[{ name: "Home", to: "/" }, { name: "Business" }]}
      />

      <Section>
        <div className="grid md:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="h-full bg-white p-8 md:p-10 border border-navy/10 hover:shadow-lg transition-shadow duration-300">
                <s.icon className="w-8 h-8 text-orange" strokeWidth={1.25} />
                <h3 className="mt-6 font-serif text-2xl text-navy">{s.name}</h3>
                <p className="mt-3 font-sans text-base text-slate leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section cream>
        <SectionHead label="Why a single desk" title="Your company's balance sheet is one system. We treat it that way." intro="Credit, treasury and insurance decisions affect each other. Managing them in silos leaves value — and safety — on the table." />
        <div className="flex flex-wrap gap-4">
          <CTALink to="/products/fixed-income-insurance" variant="secondary" data-testid="biz-bonds-cta">Fixed Income & Insurance</CTALink>
          <CTALink to="/contact" variant="primary" data-testid="biz-contact-cta">Talk to us</CTALink>
        </div>
      </Section>

      <Section>
        <div className="max-w-3xl"><SectionHead label="FAQ" title="Questions from business owners" /><FAQ items={faq} /></div>
      </Section>

      <Section dark>
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <SectionHead label="Next step" title="Tell us where your business is headed." dark intro="We'll map the financing, treasury and cover that fits — with the trade-offs made explicit." />
          <div className="bg-white/5 border border-white/10 p-8 md:p-10">
            <LeadForm dark audience="business" title="Request a business consultation" />
          </div>
        </div>
      </Section>
    </div>
  );
}
